import styled, { css } from 'styled-components';
import { Icon } from 'src/components';

export const Page = styled.main<{ $hasMedia: boolean }>(
  ({ theme, $hasMedia }) => css`
    display: grid;
    grid-template-columns: ${$hasMedia ? 'minmax(0, 1.05fr) minmax(0, 0.95fr)' : '1fr'};
    height: 100dvh;
    width: 100%;
    background: ${theme.co.primary99};
    color: ${theme.co.secondary20};
    overflow: hidden;

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: 1fr;
      grid-template-rows: ${$hasMedia ? 'minmax(16rem, 38vh) minmax(0, 1fr)' : '1fr'};
    }
  `,
);

export const Media = styled.div(
  ({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    min-height: 0;
    height: 100%;
    overflow: hidden;
    padding: ${theme.sz.s6};
    background: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s4};
    }
  `,
);

export const Still = styled.video(
  () => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  `,
);

export const Quote = styled.figure(
  ({ theme }) => css`
    position: relative;
    z-index: ${theme.zi.z1};
    width: 100%;
    max-width: ${theme.sz.m};
    margin: 0;
    padding: ${theme.sz.s5};
    border-radius: ${theme.bo.mediumLarge};
    background: ${theme.co.secondary20};
    color: ${theme.co.neutral99};

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s4};
    }
  `,
);

export const Stars = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s1};
    margin-bottom: ${theme.sz.s3};
    color: ${theme.co.primary20};

    svg {
      width: ${theme.sz.s4};
      height: ${theme.sz.s4};
    }
  `,
);

export const QuoteLead = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    margin: 0 0 ${theme.sz.s3};
    color: ${theme.co.neutral99};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const QuoteBody = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    margin: 0 0 ${theme.sz.s4};
    font-weight: 700;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const QuoteAuthor = styled.figcaption(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary80};
  `,
);

export const Panel = styled.section(
  ({ theme }) => css`
    /* GlobalStyle caps section at 70ex, which pins this column to the left. */
    max-width: none;
    width: 100%;
    min-height: 0;
    height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${theme.sz.s8} ${theme.sz.s6} ${theme.sz.s6};
    background: ${theme.co.primary99};

    @media (max-width: ${theme.sz.lg}) {
      padding: ${theme.sz.s5} ${theme.sz.s4} ${theme.sz.s6};
    }
  `,
);

export const PanelContent = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s4};
    width: 100%;
    max-width: ${theme.sz.ms};
    margin-inline: auto;
    margin-block: auto;

    @media (max-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s3};
    }
  `,
);

export const Headline = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.m};
    color: ${theme.co.secondary20};
    margin: 0 0 ${theme.sz.s2};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.m};
    }
  `,
);

export const PlanList = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
  `,
);

export const PlanOption = styled.button<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${theme.sz.s4};
    width: 100%;
    padding: ${theme.sz.s4} ${theme.sz.s5};
    border-radius: ${theme.bo.medium};
    border: 2px solid ${theme.co.secondary90};
    background: ${theme.co.neutral99};
    cursor: pointer;
    text-align: start;

    &:hover {
      border-color: ${theme.co.primary50};
    }

    ${
      $selected &&
      css`
        border-color: ${theme.co.primary20};
        background: ${theme.co.primary98};

        &:hover {
          border-color: ${theme.co.primary20};
        }
      `
    }
  `,
);

export const Radio = styled.span<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.sz.s6};
    height: ${theme.sz.s6};
    border-radius: ${theme.bo.elliptical};
    border: 2px solid ${$selected ? theme.co.secondary20 : theme.co.secondary70};
    background: ${$selected ? theme.co.secondary20 : theme.co.neutral99};
    color: ${theme.co.neutral99};
  `,
);

export const RadioCheck = styled(Icon)(
  ({ theme }) => css`
    width: ${theme.sz.s4};
    height: ${theme.sz.s4};
  `,
);

export const PlanCopy = styled.span(
  () => css`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  `,
);

export const PlanHeading = styled.span(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.sz.s2};
  `,
);

export const SaveBadge = styled.span(
  ({ theme }) => css`
    display: inline-block;
    padding: ${theme.sz.s05} ${theme.sz.s2};
    border-radius: ${theme.bo.small};
    background: ${theme.co.success50};
    color: ${theme.co.neutral99};
    ${theme.typography.paragraph.s};
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `,
);

export const StruckPrice = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary60};
    font-weight: 600;
    text-decoration: line-through;
  `,
);

export const PlanLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const PlanDetail = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary50};
  `,
);

export const PriceColumn = styled.span(
  () => css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
  `,
);

export const PlanPrice = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xs};
    color: ${theme.co.secondary20};
    font-variant-numeric: tabular-nums;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.xs};
    }
  `,
);

export const PriceSuffix = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary50};
  `,
);

export const PopularBadge = styled.span(
  ({ theme }) => css`
    position: absolute;
    top: -${theme.sz.s3};
    right: ${theme.sz.s4};
    padding: ${theme.sz.s1} ${theme.sz.s3};
    border-radius: ${theme.bo.small};
    background: ${theme.co.secondary20};
    color: ${theme.co.neutral99};
    ${theme.typography.paragraph.s};
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  `,
);

export const Benefits = styled.ul(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s2};
    margin: ${theme.sz.s2} 0;
    padding: 0;
    list-style: none;
  `,
);

export const Benefit = styled.li(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.sz.s3};
  `,
);

export const BenefitCheck = styled(Icon)(
  ({ theme }) => css`
    flex-shrink: 0;
    width: ${theme.sz.s5};
    height: ${theme.sz.s5};
    margin-top: ${theme.sz.s05};
    font-size: ${theme.sz.s5};
    color: ${theme.co.primary10};
  `,
);

export const BenefitCopy = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    color: ${theme.co.secondary20};
    font-weight: 600;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const BenefitNote = styled.span(
  ({ theme }) => css`
    font-weight: 500;
    color: ${theme.co.secondary40};
  `,
);

export const Reassurance = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    margin: 0;
    color: ${theme.co.secondary50};
    text-align: center;
  `,
);

export const NotReady = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    margin: 0;
    color: ${theme.co.secondary40};
    text-align: center;
  `,
);

export const KeepLearning = styled.button(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    padding: 0;
    border: none;
    background: none;
    color: ${theme.co.secondary40};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 0.18em;
    cursor: pointer;

    &:hover {
      color: ${theme.co.secondary20};
    }
  `,
);
